import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ImageLightbox from './Gallery/ImageLightbox';
import UploadPhotoModal from '../Modals/UploadPhotoModal';

const { FiPlus, FiMaximize2, FiImage, FiZap, FiCamera, FiLayout, FiGrid } = FiIcons;

const TabGallery = ({ memorial }) => {
  const [galleryImages, setGalleryImages] = useState(memorial.gallery || []);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setIsLightboxOpen(true);
  };

  const handleNewUpload = (newImages) => {
    setGalleryImages(prev => [...newImages, ...prev]);
    
    // Broadcast notification event for new photo uploads
    window.dispatchEvent(new CustomEvent('memorial_activity', {
      detail: {
        type: 'photo',
        count: newImages.length,
        memorialId: memorial.id,
        timestamp: new Date().toISOString()
      }
    }));
  };

  return (
    <div className="space-y-10">
      <div className="bg-white p-8 md:p-10 rounded-[40px] shadow-sm border border-brand-dark flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-accent-gold/10 rounded-2xl flex items-center justify-center text-accent-gold shadow-sm">
            <SafeIcon icon={FiLayout} className="w-7 h-7" />
          </div>
          <div>
            <h3 className="font-serif text-2xl text-accent-navy">Captured Moments</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">{galleryImages.length} Photographs Shared</p>
          </div>
        </div>
        <button 
          onClick={() => setIsUploadOpen(true)}
          className="w-full md:w-auto btn-accent px-10 py-4 flex items-center justify-center gap-3 text-lg group"
        >
          <SafeIcon icon={FiPlus} className="group-hover:rotate-90 transition-transform" /> 
          Add Memories
        </button>
      </div>

      {galleryImages.length > 0 ? (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
          {galleryImages.map((img, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="relative rounded-[32px] overflow-hidden group cursor-pointer border border-brand-dark shadow-sm bg-white"
              onClick={() => openLightbox(index)}
            >
              <img 
                src={img} 
                alt={`Memory ${index + 1}`} 
                className="w-full h-auto object-cover transition-transform duration-1000 group-hover:scale-110" 
              />
              <div className="absolute inset-0 bg-accent-navy/40 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-accent-navy shadow-xl scale-90 group-hover:scale-100 transition-transform duration-300">
                  <SafeIcon icon={FiMaximize2} className="w-6 h-6" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-32 bg-white rounded-[50px] border-2 border-dashed border-brand-dark">
          <div className="w-24 h-24 bg-brand-light rounded-3xl flex items-center justify-center text-text-muted/20 mx-auto mb-8 border border-brand-dark/50">
            <SafeIcon icon={FiImage} className="w-12 h-12" />
          </div>
          <h4 className="text-3xl font-serif text-accent-navy mb-4">A Blank Canvas</h4>
          <p className="text-text-muted max-w-sm mx-auto font-light mb-10 leading-relaxed">
            Help honor this beautiful life by sharing the first memory in the gallery.
          </p>
          <button onClick={() => setIsUploadOpen(true)} className="btn-primary px-12 py-4">Upload First Photo</button>
        </div>
      )}

      <motion.div 
        whileHover={{ y: -5 }}
        className="bg-gradient-to-r from-accent-navy to-accent-navy/90 text-white p-10 md:p-14 rounded-[50px] flex flex-col lg:flex-row items-center justify-between gap-10 shadow-2xl relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-accent-gold/10 rounded-full -mr-32 -mt-32 blur-3xl opacity-50" />
        <div className="max-w-xl relative z-10 text-center lg:text-left">
          <div className="inline-flex items-center gap-2 bg-accent-gold/20 text-accent-gold px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-6 border border-accent-gold/30">
            <SafeIcon icon={FiZap} className="w-3 h-3" /> Digital Preservation
          </div>
          <h4 className="font-serif text-3xl md:text-4xl mb-6">Restore Physical Memories</h4>
          <p className="text-white/70 text-lg font-light leading-relaxed">
            Have old, faded physical photographs? Our dedicated AI restoration tool can gently remove grain, fix colors, and upscale physical memories for digital eternity.
          </p>
        </div>
        <button className="relative z-10 bg-white text-accent-navy hover:bg-accent-gold hover:text-white px-12 py-5 rounded-2xl font-black uppercase tracking-widest text-xs transition-all shadow-xl active:scale-95 flex items-center gap-3">
          <SafeIcon icon={FiCamera} /> Try AI Restorer
        </button>
      </motion.div>

      <ImageLightbox 
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        images={galleryImages}
        currentIndex={currentIndex}
        onNavigate={setCurrentIndex}
      />

      <UploadPhotoModal 
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onUpload={handleNewUpload}
      />
    </div>
  );
};

export default TabGallery;